const pad = (value: number) => value.toString().padStart(2, "0");

const toLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  return `${year}-${month}-${day}`;
};

const getNthWeekdayOfMonth = (
  year: number,
  monthIndex: number,
  weekday: number,
  nth: number
): Date => {
  const firstOfMonth = new Date(year, monthIndex, 1);
  const firstWeekdayOffset = (weekday - firstOfMonth.getDay() + 7) % 7;
  const dayOfMonth = 1 + firstWeekdayOffset + (nth - 1) * 7;
  return new Date(year, monthIndex, dayOfMonth);
};

const getLastWeekdayOfMonth = (
  year: number,
  monthIndex: number,
  weekday: number
): Date => {
  const lastOfMonth = new Date(year, monthIndex + 1, 0);
  const offset = (lastOfMonth.getDay() - weekday + 7) % 7;
  const dayOfMonth = lastOfMonth.getDate() - offset;
  return new Date(year, monthIndex, dayOfMonth);
};

const observeFixedHoliday = (date: Date): Date => {
  const day = date.getDay();
  if (day === 6) {
    // Saturday -> Friday
    const observed = new Date(date);
    observed.setDate(date.getDate() - 1);
    return observed;
  }
  if (day === 0) {
    // Sunday -> Monday
    const observed = new Date(date);
    observed.setDate(date.getDate() + 1);
    return observed;
  }
  return date;
};

const getUsBankHolidays = (year: number): Set<string> => {
  const holidays: Date[] = [];

  // New Year's Day (Jan 1)
  holidays.push(observeFixedHoliday(new Date(year, 0, 1)));
  // Martin Luther King Jr. Day (3rd Monday in Jan)
  holidays.push(getNthWeekdayOfMonth(year, 0, 1, 3));
  // Washington's Birthday (3rd Monday in Feb)
  holidays.push(getNthWeekdayOfMonth(year, 1, 1, 3));
  // Memorial Day (last Monday in May)
  holidays.push(getLastWeekdayOfMonth(year, 4, 1));
  // Juneteenth (Jun 19)
  holidays.push(observeFixedHoliday(new Date(year, 5, 19)));
  // Independence Day (Jul 4)
  holidays.push(observeFixedHoliday(new Date(year, 6, 4)));
  // Labor Day (1st Monday in Sep)
  holidays.push(getNthWeekdayOfMonth(year, 8, 1, 1));
  // Columbus Day (2nd Monday in Oct)
  holidays.push(getNthWeekdayOfMonth(year, 9, 1, 2));
  // Veterans Day (Nov 11)
  holidays.push(observeFixedHoliday(new Date(year, 10, 11)));
  // Thanksgiving Day (4th Thursday in Nov)
  holidays.push(getNthWeekdayOfMonth(year, 10, 4, 4));
  // Christmas Day (Dec 25)
  holidays.push(observeFixedHoliday(new Date(year, 11, 25)));

  return new Set(holidays.map(toLocalDateKey));
};

const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

export const addBusinessDays = (start: Date, days: number): Date => {
  const result = new Date(start);
  const holidayCache = new Map<number, Set<string>>();

  const isHoliday = (date: Date): boolean => {
    const year = date.getFullYear();
    if (!holidayCache.has(year)) {
      holidayCache.set(year, getUsBankHolidays(year));
    }
    return holidayCache.get(year)!.has(toLocalDateKey(date));
  };

  let remaining = days;
  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    if (isWeekend(result) || isHoliday(result)) {
      continue;
    }
    remaining -= 1;
  }

  return result;
};
